// frontend/src/pages/Profile.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import useEntitlement from "../hooks/useEntitlement";
import { Save, Trash2, CreditCard, User as UserIcon, AlertCircle, Lock } from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const DEFAULTS_KEY = "aifine_profile_defaults";
const CONFIG_KEY = "aifine_config";
const USER_KEY = "aifine_user";


function normalizeFunctionsBaseUrl(value, supabaseUrl) {
  const primary = String(value || "").trim().replace(/\/$/, "");
  const fallback = String(supabaseUrl || "").trim().replace(/\/$/, "");
  const raw = primary || fallback;
  if (!raw) return "";

  try {
    const parsed = new URL(raw);

    if (/\.functions\.supabase\.co$/i.test(parsed.hostname)) {
      const projectRef = parsed.hostname.split(".")[0];
      return `https://${projectRef}.supabase.co/functions/v1`;
    }

    if (/\/functions\/v1$/i.test(parsed.pathname)) {
      return `${parsed.origin}${parsed.pathname}`;
    }

    return `${parsed.origin}${parsed.pathname}/functions/v1`.replace(/\/\/functions\/v1$/, "/functions/v1");
  } catch {
    return /\/functions\/v1$/i.test(raw) ? raw : `${raw}/functions/v1`;
  }
}

function buildFunctionsBaseCandidates(functionsBase, supabaseUrl) {
  const candidates = [
    normalizeFunctionsBaseUrl(functionsBase, supabaseUrl),
    normalizeFunctionsBaseUrl("", supabaseUrl),
    normalizeFunctionsBaseUrl(functionsBase, ""),
  ].filter(Boolean);

  return Array.from(new Set(candidates));
}

async function parseResponsePayload(res) {
  const fallback = {
    ok: false,
    json: null,
    text: "",
  };

  if (!res) return fallback;

  try {
    const text = await res.text();
    if (!text) {
      return {
        ok: true,
        json: null,
        text: "",
      };
    }

    try {
      return {
        ok: true,
        json: JSON.parse(text),
        text,
      };
    } catch {
      return {
        ok: true,
        json: null,
        text,
      };
    }
  } catch {
    return fallback;
  }
}

function safeParse(raw, fallback) {
  try {
    const v = JSON.parse(raw);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function normalizeDefaults(d) {
  return {
    clientName: d?.clientName ?? "",
    jurisdiction: d?.jurisdiction ?? "",
    annualTurnover: Number(d?.annualTurnover ?? 100000),
    userImpact: Number(d?.userImpact ?? 100),
  };
}

/**
 * Log-scale mapping: sliderRaw [0..1000] -> value in [min..max]
 * Low-end is more granular.
 */
function rawToLogValue(raw, min, max) {
  const r = clamp(Number(raw), 0, 1000) / 1000;
  const minLog = Math.log10(min);
  const maxLog = Math.log10(max);
  const vLog = minLog + (maxLog - minLog) * r;
  return Math.pow(10, vLog);
}

function logValueToRaw(value, min, max) {
  const v = clamp(Number(value), min, max);
  const minLog = Math.log10(min);
  const maxLog = Math.log10(max);
  const vLog = Math.log10(v);
  const r = (vLog - minLog) / (maxLog - minLog);
  return Math.round(clamp(r, 0, 1) * 1000);
}

function roundNice(n, step) {
  const s = Number(step) || 1;
  return Math.round(n / s) * s;
}

function safeUpper(s) {
  return String(s || "").trim().toUpperCase();
}

function currencyForJurisdiction(j) {
  if (!j) return null;
  const upper = safeUpper(j);

  // Accept both codes and labels
  if (upper.includes("UK")) return { code: "GBP", symbol: "£" };
  if (upper.includes("EU")) return { code: "EUR", symbol: "€" };
  if (upper.includes("US")) return { code: "USD", symbol: "$" };
  return { code: "", symbol: "" };
}

function formatPlanLabel(planKeyOrName) {
  const s = String(planKeyOrName || "").trim();
  if (!s) return null;

  const k = s.toLowerCase();
  if (k === "enterprise") return "Enterprise";
  if (k === "pro") return "Pro";
  if (k === "top") return "Global Pro";
  if (k === "standard") return "Standard";
  if (k === "single") return "Standard";
  if (k === "precision_pass") return "Precision Pass";

  return s
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function readLocalUser() {
  try {
    return safeParse(localStorage.getItem(USER_KEY), null);
  } catch {
    return null;
  }
}

function tryReadPortalUrl(userObj) {
  // Defensive: support multiple likely shapes
  const candidates = [
    userObj?.billing_portal_url,
    userObj?.billingPortalUrl,
    userObj?.customer_portal_url,
    userObj?.customerPortalUrl,
    userObj?.stripe_portal_url,
    userObj?.stripePortalUrl,
    userObj?.portal_url,
    userObj?.portalUrl,
  ];
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
  }
  return null;
}

function tryReadRenewalDate(userObj) {
  // Common Stripe patterns: current_period_end (unix seconds), currentPeriodEnd, renewal_at_iso
  const unix =
    userObj?.current_period_end ??
    userObj?.currentPeriodEnd ??
    userObj?.subscription_current_period_end ??
    userObj?.subscriptionCurrentPeriodEnd ??
    null;

  if (Number.isFinite(Number(unix)) && Number(unix) > 0) {
    const ms = Number(unix) < 10_000_000_000 ? Number(unix) * 1000 : Number(unix);
    const d = new Date(ms);
    if (!Number.isNaN(d.getTime())) return d;
  }

  const iso =
    userObj?.renewal_at_iso ??
    userObj?.renewalAtIso ??
    userObj?.current_period_end_iso ??
    userObj?.currentPeriodEndIso ??
    null;

  if (iso) {
    const d = new Date(String(iso));
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
}

function isNonEmptyString(s) {
  return typeof s === "string" && s.trim().length > 0;
}

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const entitlement = useEntitlement?.() || {};
  const entLoading = Boolean(entitlement.loading);

  // Read user + defaults early
  const localUser = useMemo(() => readLocalUser(), []);
  const portalUrl = useMemo(() => tryReadPortalUrl(localUser || {}), [localUser]);
  const renewalDate = useMemo(() => tryReadRenewalDate(localUser || {}), [localUser]);

  const entTierRaw = entitlement?.tier != null ? String(entitlement.tier) : null;
  const entPlanKey =
    entitlement?.rawPlanKey ??
    entitlement?.planKey ??
    entitlement?.plan_key ??
    entitlement?.plan ??
    null;

  const planLabel = useMemo(() => {
    const fromEnt = entPlanKey != null ? formatPlanLabel(entPlanKey) : null;
    if (fromEnt) return fromEnt;

    const fromLocal = formatPlanLabel(
      localUser?.raw_tier ?? localUser?.plan_key ?? localUser?.planKey ?? localUser?.subscription_plan ?? null
    );
    if (fromLocal) return fromLocal;

    return formatPlanLabel(entTierRaw) || "—";
  }, [entPlanKey, entTierRaw, localUser]);

  const accountScopeLabel = useMemo(() => {
    const t =
      localUser?.subscription_tier ??
      localUser?.subscriptionTier ??
      localUser?.tier ??
      entTierRaw ??
      "";
    const k = String(t || "").toLowerCase();
    if (k === "top") return "Multiple businesses";
    return "Single business";
  }, [localUser, entTierRaw]);

  // ----- Defaults form -----
  const [formData, setFormData] = useState(() => {
    const saved = safeParse(localStorage.getItem(DEFAULTS_KEY), null);
    return normalizeDefaults(saved);
  });

  // Enforce "entity name lock" at UI/storage level:
  // - If a name already exists in saved defaults OR config, treat it as locked and read-only.
  const [lockedEntityName, setLockedEntityName] = useState("");
  const [billingState, setBillingState] = useState({ loading: false, error: "", info: "" });
  const [cancelMarketingOptIn, setCancelMarketingOptIn] = useState(false);
  const [consentState, setConsentState] = useState({ loading: false, loaded: false, error: "" });
  const [subscriptionRowId, setSubscriptionRowId] = useState(null);

  useEffect(() => {
    const savedDefaults = safeParse(localStorage.getItem(DEFAULTS_KEY), null);
    const cfg = safeParse(localStorage.getItem(CONFIG_KEY), null);

    const savedName = isNonEmptyString(savedDefaults?.clientName) ? String(savedDefaults.clientName).trim() : "";
    const cfgName = isNonEmptyString(cfg?.clientName) ? String(cfg.clientName).trim() : "";

    const locked = savedName || cfgName || "";
    setLockedEntityName(locked);

    // Hydrate defaults from active audit config only if no saved profile exists yet
    if (savedDefaults) return;
    if (!cfg) return;

    setFormData(
      normalizeDefaults({
        clientName: cfg.clientName,
        jurisdiction: Array.isArray(cfg.jurisdictions) ? cfg.jurisdictions[0] : cfg.jurisdiction,
        annualTurnover: cfg.annualTurnover,
        userImpact: cfg.userImpact,
      })
    );
  }, []);

  const isEntityLocked = useMemo(() => isNonEmptyString(lockedEntityName), [lockedEntityName]);

  // ---- Annual Turnover slider (100k .. 500m) log scale ----
  const TURNOVER_MIN = 100_000;
  const TURNOVER_MAX = 500_000_000;

  const turnoverRaw = useMemo(
    () => logValueToRaw(formData.annualTurnover, TURNOVER_MIN, TURNOVER_MAX),
    [formData.annualTurnover]
  );

  // ---- User Impact slider (100 .. 1,000,000) log scale ----
  const IMPACT_MIN = 100;
  const IMPACT_MAX = 1_000_000;

  const impactRaw = useMemo(
    () => logValueToRaw(formData.userImpact, IMPACT_MIN, IMPACT_MAX),
    [formData.userImpact]
  );

  const cur = useMemo(() => currencyForJurisdiction(formData.jurisdiction), [formData.jurisdiction]);

  const turnoverDisplay = useMemo(() => {
    const symbol = cur?.symbol || "";
    return `${symbol}${Number(formData.annualTurnover || 0).toLocaleString("en-US")}`;
  }, [formData.annualTurnover, cur]);

  const impactDisplay = useMemo(() => Number(formData.userImpact || 0).toLocaleString("en-US"), [formData.userImpact]);

  const handleSaveProfile = () => {
    const nextClientName = (formData.clientName || "").trim();

    // Lock rule: if we already have a locked name, it must not change.
    if (isEntityLocked) {
      const locked = String(lockedEntityName || "").trim();
      if (nextClientName && nextClientName !== locked) {
        alert(
          "Legal Entity Name is locked for this account in this browser. To prevent misuse, it cannot be changed here."
        );
        // restore UI value to locked name
        setFormData((prev) => ({ ...prev, clientName: locked }));
        return;
      }
    }

    // If no locked name exists yet, allow setting it once (but do not require it).
    const profile = {
      clientName: nextClientName,
      jurisdiction: formData.jurisdiction || "",
      annualTurnover: Number(formData.annualTurnover),
      userImpact: Number(formData.userImpact),
      updated_at_iso: new Date().toISOString(),
    };

    try {
      localStorage.setItem(DEFAULTS_KEY, JSON.stringify(profile));
      if (!isEntityLocked && nextClientName) setLockedEntityName(nextClientName);

      alert("Saved. Defaults will prefill Start Audit.");
    } catch {
      alert("Unable to save. Browser storage may be blocked or full.");
    }
  };

  useEffect(() => {
    try {
      const qs = new URLSearchParams(location.search || "");
      if (qs.get("billing") === "return") {
        setBillingState({
          loading: false,
          error: "",
          info: "Returned from Stripe Billing. Any subscription change may take a short moment to appear here.",
        });
      }
    } catch {}
  }, [location.search]);


  useEffect(() => {
    let mounted = true;

    const loadCancellationMarketingPreference = async () => {
      setConsentState({ loading: true, loaded: false, error: "" });

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError || !user?.id) {
          if (!mounted) return;
          setConsentState({ loading: false, loaded: true, error: "" });
          return;
        }

        const { data, error } = await supabase
          .from("subscriptions")
          .select("id, marketing_opt_in")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(1);

        if (!mounted) return;

        if (error) {
          setConsentState({ loading: false, loaded: true, error: "Unable to load communication preference right now." });
          return;
        }

        const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setSubscriptionRowId(row?.id ?? null);
        setCancelMarketingOptIn(Boolean(row?.marketing_opt_in));
        setConsentState({ loading: false, loaded: true, error: "" });
      } catch {
        if (!mounted) return;
        setConsentState({ loading: false, loaded: true, error: "Unable to load communication preference right now." });
      }
    };

    loadCancellationMarketingPreference();

    return () => {
      mounted = false;
    };
  }, []);

  const persistCancellationMarketingPreference = async () => {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user?.id) return;

      let targetRowId = subscriptionRowId;

      if (!targetRowId) {
        const { data, error } = await supabase
          .from("subscriptions")
          .select("id")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(1);

        if (error) return;

        const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
        targetRowId = row?.id ?? null;
        if (targetRowId) {
          setSubscriptionRowId(targetRowId);
        }
      }

      if (!targetRowId) return;

      const payload = {
        marketing_opt_in: Boolean(cancelMarketingOptIn),
        marketing_opt_in_source: "cancellation",
        marketing_opt_in_at: cancelMarketingOptIn ? new Date().toISOString() : null,
      };

      await supabase.from("subscriptions").update(payload).eq("id", targetRowId);
    } catch {
      // Non-blocking by design. Billing access should continue even if preference persistence fails.
    }
  };

  const clearAllData = () => {
    const ok = window.confirm(
      "This will clear all locally stored data in this browser (audits, snapshots/history, defaults, config, and in-progress answers). This cannot be undone. Proceed?"
    );
    if (!ok) return;

    try {
      localStorage.clear();
    } catch {}
    try {
      window.location.assign("/dashboard");
    } catch {
      navigate("/dashboard", { replace: true });
    }
  };

  const manageBilling = async () => {
    if (billingState.loading) return;

    await persistCancellationMarketingPreference();

    if (portalUrl) {
      try {
        window.location.assign(portalUrl);
        return;
      } catch {}
    }

    setBillingState({ loading: true, error: "", info: "Opening secure billing portal…" });

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      const accessToken = session?.access_token || "";
      if (sessionError || !accessToken) {
        setBillingState({
          loading: false,
          error: "Please sign in again to manage billing.",
          info: "",
        });
        navigate("/auth?next=%2Fprofile");
        return;
      }

      const functionsBaseCandidates = buildFunctionsBaseCandidates(
        process.env.REACT_APP_SUPABASE_FUNCTIONS_BASE,
        process.env.REACT_APP_SUPABASE_URL
      );

      let portalUrlFromApi = "";
      let lastError = "";

      for (const base of functionsBaseCandidates) {
        try {
          const res = await fetch(`${base}/create_portal_session`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || "",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              return_url: `${window.location.origin}/profile`,
            }),
          });

          const payload = await parseResponsePayload(res);
          const json = payload.json || {};

          if (res.ok && typeof json?.portal_url === "string" && json.portal_url.trim()) {
            portalUrlFromApi = json.portal_url.trim();
            break;
          }

          lastError =
            String(json?.details || json?.error || payload.text || `Billing portal request failed (${res.status})`).trim();
        } catch (err) {
          lastError = err instanceof Error ? err.message : "Billing portal request failed";
        }
      }

      if (!portalUrlFromApi) {
        setBillingState({
          loading: false,
          error: lastError || "Unable to open billing portal right now.",
          info: "",
        });
        return;
      }

      setBillingState({ loading: false, error: "", info: "Redirecting to secure billing portal…" });
      window.location.assign(portalUrlFromApi);
    } catch (err) {
      setBillingState({
        loading: false,
        error: err instanceof Error ? err.message : "Unable to open billing portal right now.",
        info: "",
      });
    }
  };

  const isPrecisionPass = String(planLabel || "").toLowerCase().includes("precision pass");

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-6xl px-8 py-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Account Settings</h1>
              <p className="mt-1 text-sm text-slate-600">
                Manage billing access, review cancellation terms, and save default inputs to speed up future audits.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Account + Billing */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Account */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-slate-100 p-3">
                  <UserIcon size={18} className="text-slate-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-black text-slate-900">Account</div>
                  <div className="mt-1 text-xs text-slate-500">Your current plan and account scope.</div>
                </div>
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-slate-600">Account Scope</div>
                  <div className="font-semibold text-slate-900">{accountScopeLabel}</div>
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-slate-600">Plan</div>
                  <div className="font-semibold text-slate-900">{planLabel}</div>
                </div>

                {entLoading ? (
                  <div className="text-xs text-slate-500">Loading entitlement…</div>
                ) : null}
              </div>
            </div>

            {/* Billing */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-emerald-50 p-3">
                  <CreditCard size={18} className="text-emerald-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-black text-slate-900">Billing</div>
                  <div className="mt-1 text-xs text-slate-500">Renewal and cancellation controls (when available).</div>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <div className="text-slate-600">Auto-renewal</div>
                  <div className="font-semibold text-slate-900">{isPrecisionPass ? "Not applicable" : "Enabled"}</div>
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <div className="text-slate-600">Next renewal</div>
                  <div className="font-semibold text-slate-900">
                    {isPrecisionPass ? "—" : renewalDate ? renewalDate.toLocaleString() : "—"}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-xs text-slate-700">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={cancelMarketingOptIn}
                      onChange={(e) => setCancelMarketingOptIn(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                    />
                    <span>
                      Keep me informed about updates, improvements, and opportunities to restore my account after cancellation.
                    </span>
                  </label>
                  <div className="mt-2 text-[11px] leading-5 text-slate-500">
                    This preference will be recorded when you continue to the Stripe Billing Portal. You can leave it unchecked if you do not want marketing communications after cancellation.
                  </div>
                  {consentState.error ? (
                    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                      {consentState.error}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={manageBilling}
                  disabled={billingState.loading || consentState.loading}
                  className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-extrabold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  title="Open Stripe Billing Portal"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <CreditCard size={16} />
                    {billingState.loading || consentState.loading ? "Opening Billing Portal..." : "Manage Billing"}
                  </span>
                </button>

                {billingState.error ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800">
                    {billingState.error}
                  </div>
                ) : null}

                {billingState.info ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                    {billingState.info}
                  </div>
                ) : null}

                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex items-start gap-2">
                  <AlertCircle size={16} className="mt-0.5" />
                  <div>
                    <div className="font-semibold">Cancellation</div>
                    <div className="mt-0.5">
                      Use the Stripe Billing Portal to manage renewal or schedule cancellation. Access remains active
                      until the end of the current billing period when Stripe is configured for period-end cancellation.
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-xs text-slate-700">
                  <div className="font-semibold text-slate-900">What happens if you cancel</div>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>Your subscription access remains active through the end of the current paid billing period, when applicable.</li>
                    <li>Your audit data, reports, and related history are retained for 60 days after cancellation so you can re-subscribe and continue where you left off.</li>
                    <li>If you do not re-subscribe within that 60-day period, your data will be permanently deleted from our active systems, subject to limited legal, billing, and security retention obligations.</li>
                    <li>If you want your data deleted sooner, please contact support and request immediate deletion.</li>
                  </ul>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-xs text-slate-600">
                  <div className="font-semibold text-slate-900">Marketing communications</div>
                  <div className="mt-1">
                    You will only receive marketing communications where you have separately opted in, or where we are otherwise permitted to contact you under applicable law.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Defaults */}
          <div className="mt-6 rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="px-8 py-6 border-b border-slate-100">
              <div className="text-sm font-black text-slate-900">Default Audit Inputs</div>
              <div className="mt-1 text-xs text-slate-500">These defaults prefill Start Audit. They do not change existing snapshots.</div>
            </div>

            <form
              className="px-8 py-8 space-y-8"
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveProfile();
              }}
            >
              {/* Entity name (locked once set) */}
              <div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Legal Entity Name</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {isEntityLocked
                        ? "Locked to prevent misuse. To change it, clear local data on this browser (not recommended) or contact support once server-backed."
                        : "Set once for this browser. This is locked after first save to prevent misuse."}
                    </div>
                  </div>
                  {isEntityLocked ? (
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-extrabold text-slate-700">
                      <Lock size={14} />
                      Locked
                    </div>
                  ) : null}
                </div>

                <input
                  value={isEntityLocked ? lockedEntityName : formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  placeholder="e.g., Example Holdings LLC"
                  disabled={isEntityLocked}
                  className={[
                    "mt-3 w-full rounded-2xl border px-4 py-3 text-sm outline-none",
                    isEntityLocked
                      ? "border-slate-200 bg-slate-100 text-slate-700 cursor-not-allowed"
                      : "border-slate-200 bg-white focus:border-slate-300",
                  ].join(" ")}
                />
              </div>

              {/* Jurisdiction */}
              <div>
                <div className="text-sm font-semibold text-slate-900">Primary Jurisdiction</div>
                <div className="mt-1 text-xs text-slate-500">Used for display defaults only. Start Audit remains authoritative for currency logic.</div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {["US (FED)", "US (STATE)", "UK", "EU"].map((region) => {
                    const on = formData.jurisdiction === region;
                    return (
                      <button
                        key={region}
                        type="button"
                        onClick={() => setFormData({ ...formData, jurisdiction: region })}
                        className={[
                          "rounded-2xl border px-4 py-3 text-xs font-extrabold tracking-widest transition-colors",
                          on ? "border-slate-900 bg-slate-50 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                        ].join(" ")}
                      >
                        {region}
                      </button>
                    );
                  })}
                </div>

                {formData.jurisdiction ? (
                  <div className="mt-3 text-xs text-slate-500">
                    Currency shown: <span className="font-semibold text-slate-700">{cur?.code || "—"}</span>
                  </div>
                ) : null}
              </div>

              {/* Annual Turnover */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-semibold text-slate-900">Annual Global Turnover (default)</div>
                  <div className="text-lg font-extrabold text-blue-700">{turnoverDisplay}</div>
                </div>

                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="1"
                  className="mt-4 w-full"
                  value={turnoverRaw}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const v = rawToLogValue(raw, TURNOVER_MIN, TURNOVER_MAX);
                    const rounded = roundNice(v, 1000);
                    setFormData({ ...formData, annualTurnover: rounded });
                  }}
                />

                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <div>{cur?.symbol ? `${cur.symbol}100k` : "100k"}</div>
                  <div>{cur?.symbol ? `${cur.symbol}500m` : "500m"}</div>
                </div>
              </div>

              {/* User Impact */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-semibold text-slate-900">People Impacted Per Month (default)</div>
                  <div className="text-lg font-extrabold text-blue-700">{impactDisplay}</div>
                </div>

                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="1"
                  className="mt-4 w-full"
                  value={impactRaw}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const v = rawToLogValue(raw, IMPACT_MIN, IMPACT_MAX);
                    const rounded = v < 2000 ? roundNice(v, 10) : v < 20000 ? roundNice(v, 100) : roundNice(v, 1000);
                    setFormData({ ...formData, userImpact: Math.max(100, rounded) });
                  }}
                />

                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <div>100</div>
                  <div>1,000,000</div>
                </div>
              </div>

              {/* Actions + Danger Zone (moved up, no Start Audit button, no dev-only copy action) */}
              <div className="pt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="submit"
                  className="rounded-2xl bg-slate-900 px-4 py-4 text-sm font-extrabold text-white hover:bg-slate-800"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <Save size={16} />
                    Save Defaults
                  </span>
                </button>

                <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-start gap-3">
                    <div className="rounded-2xl bg-white p-3 border border-red-200">
                      <AlertCircle size={18} className="text-red-700" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-black text-red-900">Danger Zone</div>
                      <div className="mt-1 text-xs text-red-900/80">
                        Use this only if you need to reset this browser (e.g., corrupted local storage, demo resets).
                        This deletes audits, snapshots/history, defaults, config, and in-progress answers.
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={clearAllData}
                    className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-red-200 bg-white px-4 py-3 text-xs font-extrabold text-red-700 hover:bg-red-100"
                  >
                    <Trash2 size={16} />
                    Purge all local data
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div className="mt-10 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">Contact Us: <a href="mailto:Info@aifineprotector.com" className="font-black text-slate-900 hover:underline">Info@aifineprotector.com</a></div>

          <div className="mt-4 pb-8 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
        </div>
      </main>
    </div>
  );
}